/**
 * Planner Férias — tela.
 *
 * Regras (ficam só aqui e no motor app/routers/planner_ferias.py):
 *  - Tudo o que aparece vem de GET /api/planner-ferias/quadro. A tela não calcula
 *    "em campo" nem a data de volta: só desenha o que o motor devolve.
 *  - Rascunho só aparece com a simulação ligada e nunca vai para a folha da Coordenação.
 *  - "Tornar oficial" grava pelo endereço do Férias e Licenças (POST /api/afastamentos)
 *    e depois apaga o rascunho. O Planner não grava afastamento por conta própria.
 *  - O estilo (inclusive o de impressão) só existe com a tela aberta.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ModuleHeader from './components/ModuleHeader';
import { useAuth } from './AuthContext';
import { API_URL } from './config';
import '@fontsource/kristi';

// ───────────────────────────────────────────────────────────── tipos

interface Props { onVoltar: () => void; }

interface Agente { id: number; nome: string; nome_curto: string; grupo: 'campo' | 'laboratorio'; }
interface Item {
  chave: string; id: number; origem: 'oficial' | 'rascunho'; agente_id: number;
  tipo: string; tipo_label: string; familia: string;
  data_inicio: string; data_fim: string; data_volta: string; dias: number; observacao: string | null;
}
interface MesInfo { mes: number; menor_oficial: number; dia_oficial: string | null; menor_simulado: number; dia_simulado: string | null; }
interface Quadro {
  ano: number; hoje: string; minimo_campo: number; total_campo: number;
  agentes: Agente[]; itens: Item[]; meses: MesInfo[]; feriados: string[];
  tipos_rascunho: { tipo: string; label: string }[];
}
type Visao = 'ano' | 's1' | 's2';
interface FormRasc { id: number | null; agente_id: number; tipo: string; inicio: string; dias: number; }

// ───────────────────────────────────────────────────────────── datas

const DIA_MS = 86400000;
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MESES_LONGOS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

function dn(iso: string): number {
  const p = iso.split('-').map(Number);
  return Math.round(Date.UTC(p[0], p[1] - 1, p[2]) / DIA_MS);
}
function isoDe(n: number): string { return new Date(n * DIA_MS).toISOString().slice(0, 10); }
function inicioMes(ano: number, mesZero: number): number { return Math.round(Date.UTC(ano, mesZero, 1) / DIA_MS); }
function dm(iso: string): string { return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`; }
function dmRef(iso: string, ano: number): string {
  return Number(iso.slice(0, 4)) === ano ? dm(iso) : `${dm(iso)}/${iso.slice(2, 4)}`;
}
function dataLonga(iso: string): string {
  return `${SEMANA[new Date(dn(iso) * DIA_MS).getUTCDay()]} ${dm(iso)}/${iso.slice(0, 4)}`;
}

// ───────────────────────────────────────────────────────────── medida de texto

let telaDeMedida: CanvasRenderingContext2D | null = null;

// Largura real de um texto na fonte da tela (desconta o aperto de 0,2 px por letra usado nas barras).
function larguraTexto(texto: string, peso: number, tamanho: number, familia: string): number {
  if (typeof document === 'undefined') return texto.length * tamanho * 0.62;
  if (!telaDeMedida) telaDeMedida = document.createElement('canvas').getContext('2d');
  if (!telaDeMedida) return texto.length * tamanho * 0.62;
  telaDeMedida.font = `${peso} ${tamanho}px ${familia}`;
  return telaDeMedida.measureText(texto).width - 0.2 * texto.length;
}

// ───────────────────────────────────────────────────────────── cores

const HACHURA = 'repeating-linear-gradient(135deg, #FFFFFF 0px, #FFFFFF 6px, #DCE5F0 6px, #DCE5F0 8px)';

function coresDoItem(it: Item, hoje: string): { background: string; color: string } {
  const passou = it.data_fim < hoje;
  if (it.familia === 'licenca') return passou ? { background: '#FBEBC8', color: '#6B4700' } : { background: '#F2B544', color: '#3D2A00' };
  if (it.familia === 'falta') return passou ? { background: '#F3D3CF', color: '#7F1D1D' } : { background: '#B42318', color: '#FFFFFF' };
  if (it.familia === 'ferias') return passou ? { background: '#D5E0EC', color: '#0D3B66' } : { background: '#0D3B66', color: '#FFFFFF' };
  return passou ? { background: '#E6EAF0', color: '#3B4654' } : { background: '#5A6675', color: '#FFFFFF' };
}

function mensagemErro(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const st = e.response?.status;
    if (st === 401) return 'Sua sessão expirou. Saia e entre de novo no sistema.';
    if (st === 403) return 'Seu usuário não tem permissão para isso.';
    const det = (e.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof det === 'string') return det;
    if (!e.response) return 'Não consegui falar com o servidor. Confira a internet e tente de novo.';
  }
  return 'Algo deu errado. Tente de novo.';
}

// ───────────────────────────────────────────────────────────── selo do agente

const CORES_SELO = ['#0F766E', '#7E22CE', '#2E7D32', '#C2185B', '#6B6F0E', '#8D4B16', '#A21CAF', '#37474F', '#00797F', '#3F51B5'];

function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
}

// Cada agente fica com uma cor fixa: a preferida vem do id; se já estiver em uso, pega a próxima livre.
// Agente novo (id maior) não muda a cor de quem já estava no quadro.
function selosDe(agentes: Agente[]): Record<number, { cor: string; letras: string }> {
  const n = CORES_SELO.length;
  const usados: boolean[] = [];
  const saida: Record<number, { cor: string; letras: string }> = {};
  agentes.slice().sort((x, y) => x.id - y.id).forEach((a) => {
    let vaga = a.id % n;
    for (let k = 0; k < n && usados[vaga]; k++) vaga = (vaga + 1) % n;
    usados[vaga] = true;
    saida[a.id] = { cor: CORES_SELO[vaga], letras: iniciaisDe(a.nome) };
  });
  return saida;
}

// ───────────────────────────────────────────────────────────── estilo

const PF_STYLE_ID = 'planner-ferias-style';
const PF_CSS = `
.pf-raiz { color: #17212B; font-size: 15px; line-height: 1.35; padding-bottom: 32px; }
.pf-raiz *, .pf-modal-fundo *, .pf-folha * { box-sizing: border-box; }
.pf-barra { display: flex; flex-wrap: wrap; align-items: center; gap: 12px 20px; margin: 16px 32px; }
.pf-grupo { display: inline-flex; align-items: center; gap: 6px; }
.pf-empurra { flex: 1 1 20px; }
.pf-btn { font: inherit; font-weight: 600; min-height: 44px; padding: 0 16px; border: 1px solid #B8C2CF; border-radius: 8px; background: #FFFFFF; color: #17212B; cursor: pointer; }
.pf-btn:hover { background: #F3F5F8; }
.pf-btn:focus-visible, .pf-seg button:focus-visible, .pf-item:focus-visible { outline: 3px solid #7FA6D0; outline-offset: 2px; }
.pf-btn[disabled] { opacity: 0.5; cursor: default; }
.pf-btn-pri { background: #0D3B66; border-color: #0D3B66; color: #FFFFFF; }
.pf-btn-pri:hover { background: #0A2F52; }
.pf-btn-sim { border: 1.5px dashed #0D3B66; color: #0D3B66; }
.pf-btn-sim.on { background: #E8EEF6; }
.pf-btn-perigo { color: #8E1B12; }
.pf-ico { width: 44px; padding: 0; display: inline-flex; align-items: center; justify-content: center; }
.pf-ano { min-width: 76px; text-align: center; font-size: 26px; font-weight: 700; }
.pf-seg { display: inline-flex; border: 1px solid #B8C2CF; border-radius: 8px; overflow: hidden; background: #FFFFFF; }
.pf-seg button { font: inherit; font-weight: 500; min-height: 44px; padding: 0 16px; border: 0; border-left: 1px solid #D9DFE7; background: #FFFFFF; color: #17212B; cursor: pointer; }
.pf-seg button:first-child { border-left: 0; }
.pf-seg button.on { background: #0D3B66; color: #FFFFFF; font-weight: 600; }
.pf-msg { margin: 0 32px 12px; padding: 10px 14px; border-radius: 8px; font-weight: 500; }
.pf-msg.erro { background: #FDECEA; border: 1px solid #E5A29B; color: #7F1D1D; }
.pf-msg.ok { background: #E7F4E8; border: 1px solid #9CCBA0; color: #14532D; }
.pf-quadro { margin: 0 32px; background: #FFFFFF; border: 1px solid #D9DFE7; border-radius: 10px; overflow-x: auto; }
.pf-quadro.sim { border: 2px dashed #0D3B66; }
.pf-quadro.espera { opacity: 0.55; }
.pf-interno { position: relative; min-width: 1000px; }
.pf-aviso-sim { position: relative; z-index: 2; padding: 10px 14px; background: #E8EEF6; border-bottom: 1px solid #B8C2CF; display: flex; flex-wrap: wrap; align-items: center; gap: 10px 16px; }
.pf-aviso-sim > div { flex: 1 1 320px; min-width: 0; }
.pf-grade { position: relative; }
.pf-fundo { position: absolute; left: 176px; right: 0; top: 0; bottom: 0; pointer-events: none; }
.pf-fundo i { position: absolute; top: 0; bottom: 0; width: 1px; background: #DDE3EA; }
.pf-fundo b { position: absolute; top: 0; bottom: 0; background: rgba(13, 59, 102, 0.06); }
.pf-cab { display: flex; position: relative; z-index: 2; background: #FFFFFF; border-bottom: 1px solid #B8C2CF; }
.pf-canto { width: 176px; flex: none; }
.pf-colunas { flex: 1 1 0; min-width: 0; display: flex; }
.pf-mes { min-width: 0; height: 44px; border-left: 1px solid #DDE3EA; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; white-space: nowrap; }
.pf-mes.hoje { background: #0D3B66; color: #FFFFFF; }
.pf-linha { display: flex; border-bottom: 1px solid #E6EAF0; }
.pf-linha.ultima { border-bottom: 0; }
.pf-linha.par { background: #F6F8FB; }
.pf-linha:hover, .pf-linha.ativa { background: #E6EFF9; }
.pf-linha:hover .pf-nome, .pf-linha.ativa .pf-nome { color: #0D3B66; }
.pf-nome { width: 176px; flex: none; padding: 0 12px; display: flex; align-items: center; gap: 10px; font-size: 17px; font-weight: 700; overflow: hidden; white-space: nowrap; }
.pf-nome > div { min-width: 0; display: flex; flex-direction: column; overflow: hidden; text-overflow: ellipsis; }
.pf-nome small { font-size: 12.5px; font-weight: 400; color: #3B4654; }
.pf-selo { flex: none; width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #FFFFFF; font-size: 13px; font-weight: 700; letter-spacing: 0.03em; }
.pf-selo.lab { background: #FFFFFF; color: #37474F; border: 2px solid #8792A2; }
.pf-trilha { flex: 1 1 0; min-width: 0; position: relative; height: 56px; }
.pf-trilha.criar { cursor: cell; }
.pf-item { position: absolute; top: 6px; height: 44px; padding: 0 5px; border: 0; border-radius: 8px; font: inherit; line-height: 1.15; letter-spacing: -0.2px; text-align: left; white-space: nowrap; overflow: hidden; cursor: pointer; }
.pf-item span { display: block; font-size: 14px; font-weight: 600; }
.pf-item em { display: block; margin-top: 2px; font-size: 12.5px; font-style: normal; font-weight: 500; }
.pf-item.rasc { border: 1.5px dashed #0D3B66; }
.pf-item.sel { box-shadow: 0 0 0 2px #FFFFFF, 0 0 0 5px #0D3B66; z-index: 1; }
.pf-campo { display: flex; position: relative; z-index: 2; background: #FFFFFF; border-top: 2px solid #17212B; border-bottom: 2px solid #17212B; }
.pf-campo .pf-nome { font-weight: 700; }
.pf-campo-cel { min-width: 0; height: 60px; border-left: 1px solid #DDE3EA; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.pf-campo-cel strong { font-size: 24px; font-weight: 700; line-height: 1.1; }
.pf-campo-cel small { font-size: 12.5px; font-weight: 600; line-height: 1.1; }
.pf-campo-cel.baixo { background: #C62828; color: #FFFFFF; }
.pf-rodape { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 22px; margin: 14px 32px 0; }
.pf-leg { display: inline-flex; align-items: center; gap: 8px; }
.pf-leg i { display: inline-block; width: 28px; height: 18px; border-radius: 4px; }
.pf-min { font-size: 20px; font-weight: 700; min-width: 22px; text-align: center; }
.pf-det { margin: 16px 32px 0; background: #FFFFFF; border: 2px solid #0D3B66; border-radius: 10px; padding: 16px 20px; display: flex; flex-wrap: wrap; align-items: center; gap: 14px 36px; }
.pf-det-quem { flex: 1 1 200px; min-width: 0; }
.pf-det-quem strong { display: block; font-size: 22px; font-weight: 700; }
.pf-det-rot { font-size: 13px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #3B4654; }
.pf-det-val { font-size: 22px; font-weight: 700; }
.pf-det-val.volta { color: #0D3B66; }
.pf-acoes { display: flex; flex-wrap: wrap; gap: 8px; }
.pf-vazio { padding: 48px 16px; text-align: center; color: #3B4654; }
.pf-modal-fundo { position: fixed; inset: 0; z-index: 1400; padding: 16px; background: rgba(23, 33, 43, 0.45); display: flex; align-items: center; justify-content: center; color: #17212B; font-size: 15px; line-height: 1.35; }
.pf-modal { width: 100%; max-width: 460px; background: #FFFFFF; border-radius: 12px; padding: 20px 22px; display: flex; flex-direction: column; gap: 14px; }
.pf-modal h2 { margin: 0; font-size: 20px; font-weight: 700; }
.pf-modal label { display: block; font-weight: 600; margin-bottom: 4px; }
.pf-modal input, .pf-modal select, .pf-assina input { font: inherit; width: 100%; min-height: 44px; padding: 0 10px; border: 1px solid #B8C2CF; border-radius: 8px; background: #FFFFFF; color: #17212B; }
.pf-modal .pf-duas { display: flex; gap: 12px; }
.pf-modal .pf-duas > div { flex: 1 1 0; min-width: 0; }
.pf-resumo { padding: 10px 12px; border-radius: 8px; background: #F3F5F8; }
.pf-resumo strong { font-weight: 700; }
.pf-modal .pf-acoes { justify-content: flex-end; }
.pf-assina { display: inline-flex; align-items: center; gap: 8px; }
.pf-assina input { width: 280px; }
.pf-dica { color: #3B4654; }

.pf-folha-palco { margin: 0 32px; background: #E3E8EF; border-radius: 10px; padding: 20px; overflow: auto; }
.pf-folha { width: 297mm; min-height: 209mm; margin: 0 auto; padding: 8mm 12mm; background: #FFFFFF; color: #111111; font-family: Arial, Helvetica, sans-serif; font-size: 10pt; line-height: 1.3; box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18); display: flex; flex-direction: column; gap: 3mm; }
.pf-f-topo { display: flex; align-items: center; gap: 5mm; padding-bottom: 2.5mm; border-bottom: 0.6mm solid #111111; }
.pf-f-topo img { height: 13mm; width: auto; display: block; }
.pf-f-org { flex: 1 1 0; min-width: 0; padding-left: 5mm; border-left: 0.3mm solid #5F6B7A; }
.pf-f-org strong { display: block; font-weight: 600; }
.pf-f-titulo { text-align: right; }
.pf-f-titulo h1 { margin: 0; font-size: 18pt; line-height: 1.15; font-weight: 700; }
.pf-f-titulo div { font-size: 10.5pt; }
.pf-f-quadro { position: relative; border: 0.3mm solid #5F6B7A; }
.pf-f-fundo { position: absolute; left: 34mm; right: 0; top: 0; bottom: 0; }
.pf-f-fundo i { position: absolute; top: 0; bottom: 0; width: 0.25mm; background: #B4BCC8; }
.pf-f-lin { display: flex; height: 5.2mm; border-bottom: 0.25mm solid #B4BCC8; position: relative; }
.pf-f-lin.cab { height: 5.2mm; background: #EEF1F5; border-bottom: 0.3mm solid #5F6B7A; font-size: 9pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.pf-f-lin.campo { height: 6.6mm; border-top: 0.6mm solid #111111; border-bottom: 0; background: #FFFFFF; }
.pf-f-nome { width: 34mm; flex: none; padding: 0 1.5mm; display: flex; align-items: center; font-weight: 600; white-space: nowrap; overflow: hidden; }
.pf-f-cols { flex: 1 1 0; min-width: 0; display: flex; position: relative; }
.pf-f-mes { min-width: 0; display: flex; align-items: center; justify-content: center; }
.pf-f-num { min-width: 6.5mm; padding: 0 1.3mm; border: 0.3mm solid #FFFFFF; background: #FFFFFF; text-align: center; font-size: 10.5pt; line-height: 4.6mm; font-weight: 500; }
.pf-f-num.baixo { border: 0.6mm solid #111111; font-weight: 700; }
.pf-f-item { position: absolute; top: 0.4mm; height: 4.2mm; padding: 0 0.6mm; border: 0.3mm solid #111111; overflow: hidden; white-space: nowrap; font-size: 8pt; font-weight: 600; line-height: 3.5mm; letter-spacing: -0.1px; }
.pf-f-item.fer { background: #3A4350; color: #FFFFFF; }
.pf-f-item.lic { background: repeating-linear-gradient(135deg, #FFFFFF 0, #FFFFFF 1mm, #9AA4B2 1mm, #9AA4B2 1.4mm); color: #111111; }
.pf-f-item.lic span { background: #FFFFFF; padding: 0 0.5mm; }
.pf-f-leg { display: flex; flex-wrap: wrap; align-items: center; gap: 1mm 5mm; font-size: 9pt; }
.pf-f-leg span { display: inline-flex; align-items: center; gap: 1.5mm; }
.pf-f-leg i { display: inline-block; width: 7mm; height: 3.2mm; border: 0.3mm solid #111111; }
.pf-f-tab { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
.pf-f-tab th { padding: 0.8mm 1.5mm; border-top: 0.6mm solid #111111; border-bottom: 0.3mm solid #111111; text-align: left; font-size: 9pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; }
.pf-f-tab td { padding: 0.35mm 1.5mm; border-bottom: 0.25mm solid #B4BCC8; }
.pf-f-tab td.forte { font-weight: 600; }
.pf-f-tab tr { break-inside: avoid; page-break-inside: avoid; }
.pf-f-fim { margin-top: auto; padding-top: 6mm; display: flex; align-items: flex-end; justify-content: center; gap: 8mm; break-inside: avoid; page-break-inside: avoid; }
.pf-f-rubrica { font-family: 'Kristi', cursive; font-size: 30px; line-height: 1; color: #1A3A8F; margin-bottom: -4px; transform: rotate(-3deg); white-space: nowrap; }
.pf-marca { display: inline-flex; align-items: center; gap: 8px; min-height: 44px; padding: 0 14px; border: 1px solid #B8C2CF; border-radius: 8px; background: #FFFFFF; font-weight: 600; cursor: pointer; }
.pf-marca input { width: 20px; height: 20px; margin: 0; accent-color: #0D3B66; cursor: pointer; }
.pf-f-fim small { font-size: 9pt; }
.pf-f-assina { width: 90mm; text-align: center; }
.pf-f-assina strong { display: block; min-height: 4.5mm; padding-top: 1mm; border-top: 0.3mm solid #111111; font-weight: 600; }
.pf-f-assina small { font-size: 9pt; }

@media print {
  @page { size: A4 landscape; margin: 0; }
  html, body { background: #FFFFFF !important; margin: 0 !important; padding: 0 !important; height: auto !important; overflow: visible !important; }
  body * { visibility: hidden !important; }
  .pf-folha, .pf-folha * { visibility: visible !important; }
  .pf-nao-imprime { display: none !important; }
  .pf-raiz { padding: 0 !important; }
  .pf-folha-palco { margin: 0 !important; background: none !important; padding: 0 !important; border-radius: 0 !important; overflow: visible !important; }
  .pf-folha { position: absolute !important; left: 0 !important; top: 0 !important; margin: 0 !important; box-shadow: none !important; min-height: 208mm !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

function instalarEstilo(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(PF_STYLE_ID)?.remove();
  const el = document.createElement('style');
  el.id = PF_STYLE_ID;
  el.appendChild(document.createTextNode(PF_CSS));
  document.head.appendChild(el);
}
function removerEstilo(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(PF_STYLE_ID)?.remove();
}

const CHAVE_ASSINATURA = 'tovis_planner_ferias_assinatura';
const ASSINATURA_PADRAO = 'Walber Reis dos Santos';
// O usuário do sistema se chama só "Supervisor": nesse caso a folha começa com o nome completo do supervisor.
function nomePadraoAssinatura(nomeUsuario?: string | null): string {
  const n = (nomeUsuario || '').trim();
  return n.split(/\s+/).length >= 2 ? n : ASSINATURA_PADRAO;
}
function lerAssinatura(padrao: string): string {
  try { return window.localStorage.getItem(CHAVE_ASSINATURA) || padrao; } catch { return padrao; }
}
function guardarAssinatura(v: string): void {
  try { window.localStorage.setItem(CHAVE_ASSINATURA, v); } catch { /* sem armazenamento: segue sem guardar */ }
}
const CHAVE_ELETRONICA = 'tovis_planner_ferias_assinatura_eletronica';
function lerEletronica(): boolean {
  try { return window.localStorage.getItem(CHAVE_ELETRONICA) === 'sim'; } catch { return false; }
}
function guardarEletronica(v: boolean): void {
  try { window.localStorage.setItem(CHAVE_ELETRONICA, v ? 'sim' : 'nao'); } catch { /* sem armazenamento: segue sem guardar */ }
}

const Seta = ({ lado }: { lado: 'esq' | 'dir' }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={lado === 'esq' ? 'M10 3 5 8l5 5' : 'm6 3 5 5-5 5'} />
  </svg>
);
const IconeModulo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4M7 14h5M7 17.5h9" />
  </svg>
);

// ───────────────────────────────────────────────────────────── folha da Coordenação

function Folha({ quadro, assinatura, eletronica }: { quadro: Quadro; assinatura: string; eletronica: boolean }) {
  const [semLogo, setSemLogo] = useState(false);
  const ano = quadro.ano;
  const d0 = inicioMes(ano, 0);
  const d1 = inicioMes(ano, 12);
  const span = d1 - d0;
  const pct = (dias: number) => `${((dias / span) * 100).toFixed(3)}%`;
  const meses = MESES.map((rot, i) => ({ rot, ini: inicioMes(ano, i), dias: inicioMes(ano, i + 1) - inicioMes(ano, i) }));
  const oficiais = quadro.itens.filter((it) => it.origem === 'oficial' && (it.familia === 'ferias' || it.familia === 'licenca'));
  const doAgente = (id: number) => oficiais.filter((it) => it.agente_id === id);
  const periodo = (it: Item) => `${dmRef(it.data_inicio, ano)} a ${dmRef(it.data_fim, ano)}`;

  return (
    <div className="pf-folha" id="planner-ferias-folha">
      <div className="pf-f-topo">
        {!semLogo && (
          <img src={`${API_URL}/api/comunicacao/logo`} alt="Secretaria Municipal de Saúde · Prefeitura de Milagres" onError={() => setSemLogo(true)} />
        )}
        <div className="pf-f-org" style={semLogo ? { paddingLeft: 0, borderLeft: 0 } : undefined}>
          {semLogo && <strong>Prefeitura Municipal de Milagres — BA · Secretaria Municipal de Saúde</strong>}
          <strong>Vigilância Epidemiológica</strong>
          <span>Agentes de Combate às Endemias</span>
        </div>
        <div className="pf-f-titulo">
          <h1>Planejamento anual de férias · {ano}</h1>
          <div>Para: Coordenação de Vigilância em Saúde</div>
        </div>
      </div>

      <div className="pf-f-quadro">
        <div className="pf-f-fundo">
          {meses.map((m) => <i key={m.rot} style={{ left: pct(m.ini - d0) }} />)}
        </div>
        <div className="pf-f-lin cab">
          <div className="pf-f-nome">Agente</div>
          <div className="pf-f-cols">
            {meses.map((m) => <div key={m.rot} className="pf-f-mes" style={{ flex: `${m.dias} 1 0px` }}>{m.rot}</div>)}
          </div>
        </div>
        {quadro.agentes.map((a) => (
          <div key={a.id} className="pf-f-lin">
            <div className="pf-f-nome">{a.nome_curto}{a.grupo === 'laboratorio' ? ' (lab.)' : ''}</div>
            <div className="pf-f-cols">
              {doAgente(a.id).map((it) => {
                const a0 = Math.max(dn(it.data_inicio), d0);
                const b0 = Math.min(dn(it.data_fim) + 1, d1);
                const largo = (b0 - a0) / span >= 0.074;
                return (
                  <div key={it.chave} className={`pf-f-item ${it.familia === 'licenca' ? 'lic' : 'fer'}`} style={{ left: pct(a0 - d0), width: pct(b0 - a0) }}>
                    {largo && <span>{dmRef(it.data_inicio, ano)}–{dmRef(it.data_fim, ano)}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div className="pf-f-lin campo">
          <div className="pf-f-nome">Em campo (de {quadro.total_campo})</div>
          <div className="pf-f-cols">
            {meses.map((m, i) => {
              const n = quadro.meses[i] ? quadro.meses[i].menor_oficial : quadro.total_campo;
              return (
                <div key={m.rot} className="pf-f-mes" style={{ flex: `${m.dias} 1 0px` }}>
                  <span className={`pf-f-num${n < quadro.minimo_campo ? ' baixo' : ''}`}>{n}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="pf-f-leg">
        <span><i style={{ background: '#3A4350' }} />Férias</span>
        <span><i style={{ background: 'repeating-linear-gradient(135deg, #FFFFFF 0, #FFFFFF 1mm, #9AA4B2 1mm, #9AA4B2 1.4mm)' }} />Licença</span>
        <span>Em campo: menor número de agentes de campo num dia útil do mês. Número com borda grossa: abaixo de {quadro.minimo_campo}.</span>
      </div>

      <table className="pf-f-tab">
        <thead>
          <tr><th style={{ width: '21%' }}>Agente</th><th style={{ width: '21%' }}>Férias</th><th style={{ width: '12%' }}>Volta</th><th>Licença</th><th style={{ width: '12%' }}>Volta</th></tr>
        </thead>
        <tbody>
          {quadro.agentes.map((a) => {
            const fer = doAgente(a.id).filter((it) => it.familia === 'ferias');
            const lic = doAgente(a.id).filter((it) => it.familia === 'licenca');
            return (
              <tr key={a.id}>
                <td className="forte">{a.nome}{a.grupo === 'laboratorio' ? ' (laboratório)' : ''}</td>
                <td>{fer.length ? fer.map(periodo).join(' e ') : 'sem férias marcadas'}</td>
                <td className="forte">{fer.length ? fer.map((it) => dmRef(it.data_volta, ano)).join(' e ') : '—'}</td>
                <td>{lic.length ? lic.map((it) => `${it.tipo_label.replace('Licença ', '').replace(/^./, (c) => c.toUpperCase())}: ${periodo(it)}`).join(' e ') : '—'}</td>
                <td className="forte">{lic.length ? lic.map((it) => dmRef(it.data_volta, ano)).join(' e ') : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="pf-f-fim">
        <div className="pf-f-assina">
          {eletronica && assinatura.trim() !== '' && <div className="pf-f-rubrica">{assinatura}</div>}
          <strong>{assinatura}</strong>
          <small>Supervisor dos Agentes de Combate às Endemias</small>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────── tela

export default function PlannerFerias({ onVoltar }: Props) {
  const { token, usuario } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [ano, setAno] = useState<number>(() => new Date().getFullYear());
  const [visao, setVisao] = useState<Visao>('ano');
  const [simular, setSimular] = useState(false);
  const [vista, setVista] = useState<'quadro' | 'folha'>('quadro');
  const [quadro, setQuadro] = useState<Quadro | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const [form, setForm] = useState<FormRasc | null>(null);
  const [confirmar, setConfirmar] = useState<Item | null>(null);
  const [assinatura, setAssinatura] = useState<string>(() => lerAssinatura(nomePadraoAssinatura(usuario?.nome)));
  const [largura, setLargura] = useState(1100);
  const [familia, setFamilia] = useState('sans-serif');
  const [eletronica, setEletronica] = useState<boolean>(() => lerEletronica());
  const [, setFontesProntas] = useState(0);

  const pedido = useRef(0);
  const colunasRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => { instalarEstilo(); return removerEstilo; }, []);

  const carregar = useCallback(async (anoAlvo: number) => {
    const meu = ++pedido.current;
    setCarregando(true);
    try {
      const r = await axios.get<Quadro>(`${API_URL}/api/planner-ferias/quadro`, { params: { ano: anoAlvo }, headers });
      if (meu !== pedido.current) return;
      setQuadro(r.data);
      setErro('');
    } catch (e) {
      if (meu !== pedido.current) return;
      setErro(mensagemErro(e));
    } finally {
      if (meu === pedido.current) setCarregando(false);
    }
  }, [headers]);

  useEffect(() => { carregar(ano); }, [ano, carregar]);

  const temQuadro = quadro !== null;
  useEffect(() => {
    const el = colunasRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    setFamilia(window.getComputedStyle(el).fontFamily || 'sans-serif');
    const ro = new ResizeObserver((entradas) => {
      const w = entradas[0]?.contentRect.width;
      if (w) setLargura(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [temQuadro, vista]);

  useEffect(() => {
    let vivo = true;
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (vivo) setFontesProntas((n) => n + 1); }).catch(() => undefined);
    }
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    if (!aviso) return undefined;
    const t = window.setTimeout(() => setAviso(''), 6000);
    return () => window.clearTimeout(t);
  }, [aviso]);

  useEffect(() => {
    if (!form && !confirmar) return undefined;
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') { setForm(null); setConfirmar(null); } };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [form, confirmar]);

  // janela de tempo visível
  const mesInicial = visao === 's2' ? 6 : 0;
  const nMeses = visao === 'ano' ? 12 : 6;
  const d0 = inicioMes(ano, mesInicial);
  const d1 = inicioMes(ano, mesInicial + nMeses);
  const span = d1 - d0;
  const pxPorDia = largura / span;
  const pct = (dias: number) => `${((dias / span) * 100).toFixed(3)}%`;
  const hoje = quadro?.hoje || isoDe(Math.floor(Date.now() / DIA_MS));
  const hojeN = dn(hoje);

  const meses = useMemo(() => {
    const lista = [];
    for (let i = 0; i < nMeses; i++) {
      const ini = inicioMes(ano, mesInicial + i);
      const fim = inicioMes(ano, mesInicial + i + 1);
      lista.push({ indice: mesInicial + i, rot: MESES[mesInicial + i], ini, dias: fim - ini, hoje: hojeN >= ini && hojeN < fim });
    }
    return lista;
  }, [ano, mesInicial, nMeses, hojeN]);

  const itensVisiveis = useMemo(
    () => (quadro ? quadro.itens.filter((it) => simular || it.origem === 'oficial') : []),
    [quadro, simular],
  );
  const itemSel = sel ? itensVisiveis.find((it) => it.chave === sel) || null : null;
  const selos = useMemo(() => selosDe(quadro ? quadro.agentes : []), [quadro]);
  const nomeDe = (id: number) => quadro?.agentes.find((a) => a.id === id)?.nome_curto || '';

  // ── ações
  const executar = async (acao: () => Promise<void>) => {
    setOcupado(true);
    setErro('');
    try { await acao(); } catch (e) { setErro(mensagemErro(e)); } finally { setOcupado(false); }
  };

  const mudarMinimo = (delta: number) => {
    if (!quadro) return;
    const novo = Math.max(0, Math.min(quadro.total_campo, quadro.minimo_campo + delta));
    if (novo === quadro.minimo_campo) return;
    executar(async () => {
      await axios.put(`${API_URL}/api/planner-ferias/config`, { minimo_campo: novo }, { headers });
      setQuadro({ ...quadro, minimo_campo: novo });
    });
  };

  const moverRascunho = (it: Item, dias: number) => executar(async () => {
    await axios.put(`${API_URL}/api/planner-ferias/rascunhos/${it.id}`, {
      data_inicio: isoDe(dn(it.data_inicio) + dias),
      data_fim: isoDe(dn(it.data_fim) + dias),
    }, { headers });
    await carregar(ano);
  });

  const excluirRascunho = (it: Item) => executar(async () => {
    await axios.delete(`${API_URL}/api/planner-ferias/rascunhos/${it.id}`, { headers });
    setSel(null);
    await carregar(ano);
  });

  const salvarForm = () => {
    if (!form) return;
    const dias = Math.max(1, Math.min(366, Math.floor(form.dias) || 1));
    const corpo = {
      agente_id: form.agente_id,
      tipo: form.tipo,
      data_inicio: form.inicio,
      data_fim: isoDe(dn(form.inicio) + dias - 1),
    };
    executar(async () => {
      const r = form.id === null
        ? await axios.post<Item>(`${API_URL}/api/planner-ferias/rascunhos`, corpo, { headers })
        : await axios.put<Item>(`${API_URL}/api/planner-ferias/rascunhos/${form.id}`, corpo, { headers });
      setForm(null);
      setSel(r.data.chave);
      await carregar(ano);
    });
  };

  const tornarOficial = (it: Item) => executar(async () => {
    await axios.post(`${API_URL}/api/afastamentos`, {
      agente_id: it.agente_id, tipo: it.tipo, data_inicio: it.data_inicio, data_fim: it.data_fim,
    }, { headers });
    setConfirmar(null);
    setSel(null);
    try {
      await axios.delete(`${API_URL}/api/planner-ferias/rascunhos/${it.id}`, { headers });
      setAviso(`Pronto: ${nomeDe(it.agente_id)} agora tem esse período registrado no Férias e Licenças.`);
    } catch {
      setAviso('O registro oficial foi criado, mas o rascunho não foi apagado. Exclua o rascunho para não contar em dobro.');
    }
    await carregar(ano);
  });

  const abrirNovo = (agenteId?: number, inicio?: string) => {
    if (!quadro || !quadro.agentes.length) return;
    setForm({
      id: null,
      agente_id: agenteId ?? quadro.agentes[0].id,
      tipo: quadro.tipos_rascunho[0]?.tipo || 'FERIAS_PLANEJADAS',
      inicio: inicio || isoDe(Math.max(hojeN, d0)),
      dias: 30,
    });
  };

  const aoClicarTrilha = (a: Agente, e: React.MouseEvent<HTMLDivElement>) => {
    if (!simular || e.target !== e.currentTarget) return;
    const caixa = e.currentTarget.getBoundingClientRect();
    const fracao = Math.min(Math.max((e.clientX - caixa.left) / caixa.width, 0), 0.9999);
    // encaixa na segunda-feira mais próxima do ponto clicado
    const dia = d0 + Math.floor(fracao * span);
    const desdeSegunda = (new Date(dia * DIA_MS).getUTCDay() + 6) % 7;
    abrirNovo(a.id, isoDe(desdeSegunda <= 3 ? dia - desdeSegunda : dia + 7 - desdeSegunda));
  };

  const alternarSimulacao = () => { setSimular((v) => !v); setSel(null); };
  const mudarAssinatura = (v: string) => { setAssinatura(v); guardarAssinatura(v); };
  const mudarEletronica = (v: boolean) => { setEletronica(v); guardarEletronica(v); };
  // Com a assinatura eletrônica marcada, só abre a impressão depois que a fonte da assinatura carregar (limite de 3 s).
  const imprimir = async () => {
    if (eletronica && typeof document !== 'undefined' && document.fonts && document.fonts.load) {
      try {
        await Promise.race([
          document.fonts.load("30px 'Kristi'"),
          new Promise((resolver) => { window.setTimeout(resolver, 3000); }),
        ]);
      } catch { /* sem a fonte, imprime assim mesmo */ }
    }
    window.print();
  };

  // ── desenho de uma linha de agente
  const linhaAgente = (a: Agente, indice: number, ultima: boolean) => (
    <div key={a.id} className={`pf-linha${indice % 2 === 1 ? ' par' : ''}${ultima ? ' ultima' : ''}${itemSel && itemSel.agente_id === a.id ? ' ativa' : ''}`}>
      <div className="pf-nome" title={a.nome}>
        <span className={`pf-selo${a.grupo === 'laboratorio' ? ' lab' : ''}`} style={a.grupo === 'laboratorio' ? undefined : { background: selos[a.id]?.cor || '#37474F' }} aria-hidden="true">{selos[a.id]?.letras || ''}</span>
        <div>
          {a.nome_curto}
          {a.grupo === 'laboratorio' && <small>laboratório</small>}
        </div>
      </div>
      <div className={`pf-trilha${simular ? ' criar' : ''}`} onClick={(e) => aoClicarTrilha(a, e)}>
        {itensVisiveis.filter((it) => it.agente_id === a.id && dn(it.data_fim) >= d0 && dn(it.data_inicio) < d1).map((it) => {
          const ini = dn(it.data_inicio);
          const fim = dn(it.data_fim);
          const a0 = Math.max(ini, d0);
          const b0 = Math.min(fim + 1, d1);
          const px = (b0 - a0) * pxPorDia;
          const rasc = it.origem === 'rascunho';
          const cores = rasc ? { background: HACHURA, color: '#0D3B66' } : coresDoItem(it, hoje);
          const raioE = ini < d0 ? '0' : '8px';
          const raioD = fim + 1 > d1 ? '0' : '8px';
          const titulo = `${a.nome_curto}: ${rasc ? 'rascunho' : it.tipo_label}, sai ${dataLonga(it.data_inicio)} e volta ${dataLonga(it.data_volta)}`;
          const linha1 = `${dmRef(it.data_inicio, ano)}–${dmRef(it.data_fim, ano)}`;
          const linha2 = `volta ${dmRef(it.data_volta, ano)}`;
          const livre = px - (rasc ? 17 : 14);
          const tam = [14, 13, 12, 11].find((s) => larguraTexto(linha1, 600, s, familia) <= livre && larguraTexto(linha2, 500, s - 1.5, familia) <= livre);
          const soInicio = !tam && larguraTexto(dm(it.data_inicio), 600, 13, familia) <= livre;
          return (
            <button
              key={it.chave}
              type="button"
              className={`pf-item${rasc ? ' rasc' : ''}${sel === it.chave ? ' sel' : ''}`}
              style={{ left: pct(a0 - d0), width: pct(b0 - a0), borderRadius: `${raioE} ${raioD} ${raioD} ${raioE}`, ...cores }}
              title={titulo}
              aria-label={titulo}
              aria-pressed={sel === it.chave}
              onClick={() => setSel(sel === it.chave ? null : it.chave)}
            >
              {tam && <span style={{ fontSize: tam }}>{linha1}</span>}
              {tam && <em style={{ fontSize: tam - 1.5 }}>{linha2}</em>}
              {soInicio && <span style={{ fontSize: 13 }}>{dm(it.data_inicio)}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const campo = quadro ? quadro.agentes.filter((a) => a.grupo === 'campo') : [];
  const laboratorio = quadro ? quadro.agentes.filter((a) => a.grupo === 'laboratorio') : [];
  const fimForm = form ? isoDe(dn(form.inicio) + Math.max(1, Math.min(366, Math.floor(form.dias) || 1)) - 1) : '';

  return (
    <div className="pf-raiz">
      <div className="pf-nao-imprime">
        <ModuleHeader
          titulo="Planner Férias"
          subtitulo="Quem está fora, quando volta e quantos ficam em campo"
          icone={<IconeModulo />}
          accentColor="#0D3B66"
          onVoltar={onVoltar}
        />

        {vista === 'quadro' ? (
          <div className="pf-barra">
            <div className="pf-grupo">
              <button type="button" className="pf-btn pf-ico" aria-label="Ano anterior" onClick={() => { setAno(ano - 1); setSel(null); }}><Seta lado="esq" /></button>
              <div className="pf-ano">{ano}</div>
              <button type="button" className="pf-btn pf-ico" aria-label="Próximo ano" onClick={() => { setAno(ano + 1); setSel(null); }}><Seta lado="dir" /></button>
            </div>
            <div className="pf-seg">
              {([['ano', 'Ano inteiro'], ['s1', '1º semestre'], ['s2', '2º semestre']] as [Visao, string][]).map(([v, rot]) => (
                <button key={v} type="button" className={visao === v ? 'on' : ''} aria-pressed={visao === v} onClick={() => setVisao(v)}>{rot}</button>
              ))}
            </div>
            <div className="pf-empurra" />
            <button type="button" className={`pf-btn pf-btn-sim${simular ? ' on' : ''}`} aria-pressed={simular} onClick={alternarSimulacao}>
              {simular ? 'Sair da simulação' : 'Simular'}
            </button>
            <button type="button" className="pf-btn pf-btn-pri" disabled={!quadro} onClick={() => setVista('folha')}>Imprimir para a Coordenação</button>
          </div>
        ) : (
          <div className="pf-barra">
            <button type="button" className="pf-btn" onClick={() => setVista('quadro')}>Voltar ao quadro</button>
            <button type="button" className="pf-btn pf-btn-pri" disabled={!quadro} onClick={imprimir}>Imprimir ou salvar em PDF</button>
            <label className="pf-assina">
              <span>Nome na assinatura</span>
              <input type="text" value={assinatura} onChange={(e) => mudarAssinatura(e.target.value)} />
            </label>
            <label className="pf-marca">
              <input type="checkbox" checked={eletronica} onChange={(e) => mudarEletronica(e.target.checked)} />
              <span>Assinatura eletrônica</span>
            </label>
            <span className="pf-dica">Para PDF, escolha “Salvar como PDF” na janela de impressão. A folha traz só o que é oficial.</span>
          </div>
        )}

        {erro && <div className="pf-msg erro" role="alert">{erro}</div>}
        {aviso && <div className="pf-msg ok" role="status">{aviso}</div>}
      </div>

      {!quadro && <div className="pf-quadro pf-nao-imprime"><div className="pf-vazio">{carregando ? 'Carregando o quadro…' : 'Não foi possível carregar o quadro.'}</div></div>}

      {quadro && vista === 'folha' && (
        <div className="pf-folha-palco">
          <Folha quadro={quadro} assinatura={assinatura} eletronica={eletronica} />
        </div>
      )}

      {quadro && vista === 'quadro' && (
        <div className="pf-nao-imprime">
          <div className={`pf-quadro${simular ? ' sim' : ''}${carregando ? ' espera' : ''}`}>
            <div className="pf-interno">
              {simular && (
                <div className="pf-aviso-sim">
                  <div><strong>Você está simulando.</strong> As barras tracejadas são rascunhos e não valem como registro. Clique num espaço vazio da linha do agente para criar um.</div>
                  <button type="button" className="pf-btn" onClick={() => abrirNovo()}>Novo rascunho</button>
                </div>
              )}
              <div className="pf-grade">
                <div className="pf-fundo">
                  {meses.filter((m) => m.hoje).map((m) => <b key={`h${m.indice}`} style={{ left: pct(m.ini - d0), width: pct(m.dias) }} />)}
                  {meses.map((m) => <i key={m.indice} style={{ left: pct(m.ini - d0) }} />)}
                </div>

                <div className="pf-cab">
                  <div className="pf-canto" />
                  <div className="pf-colunas" ref={colunasRef}>
                    {meses.map((m) => (
                      <div key={m.indice} className={`pf-mes${m.hoje ? ' hoje' : ''}`} style={{ flex: `${m.dias} 1 0px` }}>
                        {m.rot}{m.hoje ? ' · hoje' : ''}
                      </div>
                    ))}
                  </div>
                </div>

                {campo.map((a, i) => linhaAgente(a, i, false))}

                <div className="pf-campo">
                  <div className="pf-nome"><div>Em campo<small>menor nº no mês</small></div></div>
                  <div className="pf-colunas">
                    {meses.map((m) => {
                      const info = quadro.meses[m.indice];
                      const n = info ? (simular ? info.menor_simulado : info.menor_oficial) : quadro.total_campo;
                      const dia = info ? (simular ? info.dia_simulado : info.dia_oficial) : null;
                      const baixo = n < quadro.minimo_campo;
                      return (
                        <div
                          key={m.indice}
                          className={`pf-campo-cel${baixo ? ' baixo' : ''}`}
                          style={{ flex: `${m.dias} 1 0px` }}
                          title={`Em ${MESES_LONGOS[m.indice]}, o dia útil com menos gente tem ${n} de ${quadro.total_campo} agentes em campo${dia ? ` (a partir de ${dm(dia)})` : ''}.`}
                        >
                          <strong>{n}</strong>
                          {baixo && <small>abaixo</small>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {laboratorio.map((a, i) => linhaAgente(a, campo.length + i, i === laboratorio.length - 1))}
              </div>
            </div>
          </div>

          <div className="pf-rodape">
            <span className="pf-leg"><i style={{ background: '#0D3B66' }} />Férias</span>
            <span className="pf-leg"><i style={{ background: '#F2B544' }} />Licença</span>
            <span className="pf-leg"><i style={{ background: 'linear-gradient(90deg, #D5E0EC 50%, #FBEBC8 50%)' }} />Mais claro: já passou</span>
            {simular && <span className="pf-leg"><i style={{ background: HACHURA, border: '1.5px dashed #0D3B66' }} />Rascunho</span>}
            <div className="pf-empurra" />
            <span className="pf-leg">
              <i style={{ background: '#C62828' }} />
              Vermelho quando ficar abaixo de
              <button type="button" className="pf-btn pf-ico" style={{ minHeight: 40, width: 40 }} aria-label="Diminuir o mínimo em campo" disabled={ocupado} onClick={() => mudarMinimo(-1)}>−</button>
              <span className="pf-min">{quadro.minimo_campo}</span>
              <button type="button" className="pf-btn pf-ico" style={{ minHeight: 40, width: 40 }} aria-label="Aumentar o mínimo em campo" disabled={ocupado} onClick={() => mudarMinimo(1)}>+</button>
              em campo
            </span>
          </div>

          {itemSel && (
            <section className="pf-det" aria-live="polite">
              <div className="pf-det-quem">
                <strong>{nomeDe(itemSel.agente_id)}</strong>
                <span>{itemSel.origem === 'rascunho' ? `Rascunho · ${itemSel.tipo_label}` : itemSel.tipo_label} · {itemSel.dias} dias</span>
              </div>
              <div>
                <div className="pf-det-rot">Sai</div>
                <div className="pf-det-val">{dataLonga(itemSel.data_inicio)}</div>
              </div>
              <div>
                <div className="pf-det-rot">Volta</div>
                <div className="pf-det-val volta">{dataLonga(itemSel.data_volta)}</div>
              </div>
              {itemSel.origem === 'rascunho' && (
                <div className="pf-acoes">
                  <button type="button" className="pf-btn" disabled={ocupado} onClick={() => moverRascunho(itemSel, -7)}>1 semana antes</button>
                  <button type="button" className="pf-btn" disabled={ocupado} onClick={() => moverRascunho(itemSel, 7)}>1 semana depois</button>
                  <button type="button" className="pf-btn" disabled={ocupado} onClick={() => setForm({ id: itemSel.id, agente_id: itemSel.agente_id, tipo: itemSel.tipo, inicio: itemSel.data_inicio, dias: itemSel.dias })}>Editar</button>
                  <button type="button" className="pf-btn pf-btn-perigo" disabled={ocupado} onClick={() => excluirRascunho(itemSel)}>Excluir</button>
                  <button type="button" className="pf-btn pf-btn-pri" disabled={ocupado} onClick={() => setConfirmar(itemSel)}>Tornar oficial</button>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {form && quadro && (
        <div className="pf-modal-fundo pf-nao-imprime" onClick={(e) => { if (e.target === e.currentTarget) setForm(null); }}>
          <div className="pf-modal" role="dialog" aria-modal="true" aria-label={form.id === null ? 'Novo rascunho' : 'Editar rascunho'}>
            <h2>{form.id === null ? 'Novo rascunho' : 'Editar rascunho'}</h2>
            <div>
              <label htmlFor="pf-f-agente">Agente</label>
              <select id="pf-f-agente" value={form.agente_id} onChange={(e) => setForm({ ...form, agente_id: Number(e.target.value) })}>
                {quadro.agentes.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="pf-f-tipo">Tipo</label>
              <select id="pf-f-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                {quadro.tipos_rascunho.map((t) => <option key={t.tipo} value={t.tipo}>{t.label}</option>)}
              </select>
            </div>
            <div className="pf-duas">
              <div>
                <label htmlFor="pf-f-inicio">Sai em</label>
                <input id="pf-f-inicio" type="date" value={form.inicio} onChange={(e) => { if (e.target.value) setForm({ ...form, inicio: e.target.value }); }} />
              </div>
              <div>
                <label htmlFor="pf-f-dias">Quantos dias</label>
                <input id="pf-f-dias" type="number" min={1} max={366} value={form.dias} onChange={(e) => setForm({ ...form, dias: Number(e.target.value) })} />
              </div>
            </div>
            <div className="pf-resumo">Último dia fora: <strong>{dataLonga(fimForm)}</strong></div>
            {erro && <div className="pf-msg erro" role="alert" style={{ margin: 0 }}>{erro}</div>}
            <div className="pf-acoes">
              <button type="button" className="pf-btn" onClick={() => setForm(null)}>Cancelar</button>
              <button type="button" className="pf-btn pf-btn-pri" disabled={ocupado} onClick={salvarForm}>Guardar rascunho</button>
            </div>
          </div>
        </div>
      )}

      {confirmar && (
        <div className="pf-modal-fundo pf-nao-imprime" onClick={(e) => { if (e.target === e.currentTarget) setConfirmar(null); }}>
          <div className="pf-modal" role="dialog" aria-modal="true" aria-label="Tornar oficial">
            <h2>Tornar oficial?</h2>
            <div className="pf-resumo">
              <strong>{nomeDe(confirmar.agente_id)}</strong> · {confirmar.tipo_label}<br />
              Sai {dataLonga(confirmar.data_inicio)} · último dia {dataLonga(confirmar.data_fim)}<br />
              Volta {dataLonga(confirmar.data_volta)} · {confirmar.dias} dias
            </div>
            <div>Isso cria o registro no <strong>Férias e Licenças</strong> e apaga o rascunho. Para desfazer depois, exclua o registro por lá.</div>
            {erro && <div className="pf-msg erro" role="alert" style={{ margin: 0 }}>{erro}</div>}
            <div className="pf-acoes">
              <button type="button" className="pf-btn" onClick={() => setConfirmar(null)}>Cancelar</button>
              <button type="button" className="pf-btn pf-btn-pri" disabled={ocupado} onClick={() => tornarOficial(confirmar)}>Tornar oficial</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
